<template>
  <v-dialog :value="value" :max-width="showFinder ? '100%' : '440'" :fullscreen="showFinder" persistent>
    <v-card
      :dark="isDarkMode"
      class="wcd-card rounded-xl overflow-hidden d-flex flex-column"
      :class="{ 'light-theme': !isDarkMode }"
      :style="{
        transition: 'max-width 0.25s ease',
        height: showFinder ? '100vh' : 'auto',
        maxHeight: showFinder ? '100vh' : '90vh',
      }">
      <!-- The Header -->
      <div class="wcd-header">
        <div class="d-flex align-center" style="gap: 10px">
          <div class="wcd-icon" :style="{ background: data?.template.color || '#4CAF50' }">
            <v-icon small color="white">{{ localIcon || data?.template.icon || "mdi-calendar" }}</v-icon>
          </div>
          <div>
            <div class="wcd-title">{{ localTitle || data?.template.title }}</div>
            <div class="wcd-subtitle">{{ selectedEmployeeName }}</div>
          </div>
        </div>

        <v-btn icon x-small @click="$emit('input', false)" :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.4)' }">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Content Container -->
      <div
        class="d-flex flex-grow-1 overflow-hidden"
        :class="$vuetify.breakpoint.smAndDown ? 'flex-column' : 'flex-row'"
        :style="{
          minHeight: showFinder ? '0' : '480px',
        }">
        <!-- Left Column: Booking Form -->
        <div
          class="wcd-form-column d-flex flex-column"
          :style="
            $vuetify.breakpoint.smAndDown
              ? 'width: 100%'
              : {
                  width: '440px',
                  maxWidth: '440px',
                  flexShrink: 0,
                  height: showFinder ? '100%' : 'auto',
                }
          ">
          <v-card-text class="pt-4 pb-2 flex-grow-1" :class="showFinder ? 'overflow-y-auto' : 'overflow-visible'" style="scrollbar-width: thin">
            <!-- Zeitbereich Row -->
            <div class="wcd-time-row">
              <div class="wcd-time-item">
                <div class="wcd-time-label">Start</div>
                <v-text-field
                  v-if="data"
                  v-model="localStart"
                  type="time"
                  dense
                  solo
                  flat
                  :dark="isDarkMode"
                  hide-details
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                  class="wcd-time-input"></v-text-field>
              </div>
              <div class="wcd-time-item" v-if="data">
                <div class="wcd-time-label">Dauer</div>
                <v-text-field
                  v-model.number="localDuration"
                  type="number"
                  suffix="Min"
                  dense
                  solo
                  flat
                  :dark="isDarkMode"
                  hide-details
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                  class="wcd-time-input"></v-text-field>
              </div>
              <div class="wcd-time-item">
                <div class="wcd-time-label">Ende</div>
                <div class="wcd-time-result">{{ calculatedEnd }}</div>
              </div>
            </div>

            <!-- Termin-Finder Toggle Button Section (Hidden on Mobile) -->
            <div class="mt-4 mb-2" v-if="!$vuetify.breakpoint.xs">
              <v-btn block depressed class="wcd-finder-toggle-bar text-none" :class="{ active: showFinder }" @click="toggleFinder">
                <v-icon left size="16">mdi-calendar-search</v-icon>
                <span>{{ showFinder ? "Termin-Finder schließen" : "Freie Termine finden (Termin-Finder)" }}</span>
                <v-icon right size="16" class="ml-auto">{{ showFinder ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </v-btn>
            </div>

            <!-- Kunde Bereich (nur bei requiresCustomer) -->
            <div v-if="data?.template.requiresCustomer" class="mt-4 relative" style="z-index: 1000">
              <div class="wcd-time-label mb-1">Kunde *</div>

              <!-- Toggle: Account vorhanden / kein Account -->
              <div class="d-flex mb-3" style="gap: 4px">
                <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode }" @click="noAccountMode = false">
                  <v-icon x-small class="mr-1">mdi-account-search</v-icon> Kunde suchen
                </div>
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: noAccountMode }"
                  @click="
                    noAccountMode = true
                    selectedCustomer = null
                  ">
                  <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
                </div>
              </div>

              <!-- Modus 1: Normaler Kunden-Suche -->
              <template v-if="!noAccountMode">
                <user-search
                  :value="selectedCustomer"
                  @update="updateCustomer"
                  return-object
                  ignore-rights
                  label="Kunden suchen..."
                  :dark="isDarkMode"
                  outlined
                  dense
                  :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
              </template>

              <!-- Modus 2: Kein Account – Manuelle Eingabe -->
              <template v-else>
                <div class="d-flex gap-2">
                  <v-text-field v-model="noAccountInfo.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  <v-text-field v-model="noAccountInfo.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                </div>
                <v-text-field
                  v-model="noAccountInfo.phone"
                  label="Telefon *"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-phone"></v-text-field>
                <v-text-field
                  v-model="noAccountInfo.email"
                  label="E-Mail (Optional)"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-email-outline"></v-text-field>
              </template>
            </div>

            <!-- Zweite Person hinzufügen (nur bei requiresCustomer) -->
            <div v-if="data?.template.requiresCustomer && (selectedCustomer || noAccountMode)" class="mt-3">
              <div v-if="!showSecondPerson" class="d-flex align-center">
                <v-btn text x-small color="primary" @click="addSecondPerson" class="text-none">
                  <v-icon left x-small>mdi-account-plus</v-icon> Zweite Person hinzufügen (+30 Min)
                </v-btn>
              </div>
              <div v-else>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="wcd-time-label">2. Person</div>
                  <v-btn icon x-small @click="removeSecondPerson" style="color: rgba(255, 255, 255, 0.3)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>

                <div class="d-flex mb-2" style="gap: 4px">
                  <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode2 }" @click="noAccountMode2 = false">
                    <v-icon x-small class="mr-1">mdi-account-search</v-icon> Suchen
                  </div>
                  <div
                    class="wcd-toggle-btn flex-grow-1"
                    :class="{ active: noAccountMode2 }"
                    @click="
                      noAccountMode2 = true
                      selectedCustomer2 = null
                    ">
                    <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
                  </div>
                </div>

                <template v-if="!noAccountMode2">
                  <user-search
                    :value="selectedCustomer2"
                    @update="updateCustomer2"
                    return-object
                    ignore-rights
                    label="2. Kunden suchen..."
                    :dark="isDarkMode"
                    outlined
                    dense
                    :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
                </template>
                <template v-else>
                  <div class="d-flex gap-2">
                    <v-text-field v-model="noAccountInfo2.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                    <v-text-field v-model="noAccountInfo2.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  </div>
                  <v-text-field
                    v-model="noAccountInfo2.phone"
                    label="Telefon *"
                    outlined
                    dense
                    :dark="isDarkMode"
                    hide-details
                    class="mt-2"
                    prepend-inner-icon="mdi-phone"></v-text-field>
                  <v-text-field
                    v-model="noAccountInfo2.email"
                    label="E-Mail (Optional)"
                    outlined
                    dense
                    :dark="isDarkMode"
                    hide-details
                    class="mt-2"
                    prepend-inner-icon="mdi-email-outline"></v-text-field>
                </template>

                <div class="wcd-dual-badge mt-2">
                  <v-icon x-small color="primary" class="mr-1">mdi-account-multiple</v-icon>
                  <span>Dauer +30 Min ({{ localDuration }} Min total)</span>
                </div>
              </div>
            </div>

            <!-- New Contract Section -->
            <div v-if="data?.template.specialType === 'new_contract'" class="mt-4 relative" style="z-index: 1000">
              <div class="wcd-time-label mb-1">Kunde *</div>

              <!-- Toggle: Neuer Kunde / Bestehender Kunde -->
              <div class="d-flex mb-3" style="gap: 4px">
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: contractMode === 'guest' }"
                  @click="
                    contractMode = 'guest'
                    selectedCustomer = null
                  ">
                  <v-icon x-small class="mr-1">mdi-account-plus</v-icon> Neuer Kunde (Gast)
                </div>
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: contractMode === 'existing' }"
                  @click="
                    contractMode = 'existing'
                    guestData = { firstName: '', lastName: '', email: '' }
                  ">
                  <v-icon x-small class="mr-1">mdi-account-search</v-icon> Bestehender Kunde
                </div>
              </div>

              <!-- Modus 1: Neuer Kunde (Gast) -->
              <template v-if="contractMode === 'guest'">
                <div class="d-flex gap-2">
                  <v-text-field v-model="guestData.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  <v-text-field v-model="guestData.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                </div>
                <v-text-field
                  v-model="guestData.email"
                  label="E-Mail (Optional)"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-email-outline"></v-text-field>
              </template>

              <!-- Modus 2: Bestehender Kunde -->
              <template v-else>
                <user-search
                  :value="selectedCustomer"
                  @update="updateCustomer"
                  return-object
                  ignore-rights
                  label="Kunden suchen..."
                  :dark="isDarkMode"
                  outlined
                  dense
                  :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
              </template>
            </div>

            <!-- Titel-Eingabe falls freier Termin (ap1) -->
            <div v-if="data?.template?.id === 'ap1'" class="mt-4">
              <div class="wcd-time-label mb-1">Titel *</div>
              <v-text-field v-model="localTitle" outlined dense :dark="isDarkMode" hide-details placeholder="Termintitel eingeben..."></v-text-field>
            </div>

            <!-- Icon-Auswahl falls freier Termin (ap1) -->
            <div v-if="data?.template?.id === 'ap1'" class="mt-3">
              <div class="wcd-time-label mb-2">Icon</div>
              <div class="d-flex flex-wrap gap-2">
                <v-btn
                  v-for="ic in ['mdi-calendar', 'mdi-account-group', 'mdi-phone', 'mdi-laptop', 'mdi-dumbbell', 'mdi-heart-pulse']"
                  :key="ic"
                  icon
                  small
                  :color="localIcon === ic ? 'primary' : 'grey'"
                  style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)"
                  :style="localIcon === ic ? 'border-color: var(--v-primary-base)' : ''"
                  @click="localIcon = ic">
                  <v-icon small>{{ ic }}</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Notes Textarea -->
            <v-textarea
              v-if="data"
              v-model="localDesc"
              :label="noteLabel"
              outlined
              dense
              rows="2"
              :dark="isDarkMode"
              :hide-details="!isNoteRequired || !!localDesc"
              :error="isNoteRequired && !localDesc"
              :error-messages="isNoteRequired && !localDesc ? ['Pflichtfeld – bitte Notiz eingeben'] : []"
              class="mt-4"
              style="font-size: 0.85rem"></v-textarea>
          </v-card-text>

          <v-divider style="opacity: 0.05"></v-divider>

          <!-- Left Card Actions -->
          <v-card-actions class="px-5 pb-4 pt-4">
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              @click="$emit('input', false)"
              :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.5)', textTransform: 'none' }"
              >Abbrechen</v-btn
            >
            <v-btn small depressed class="wcd-save-btn" @click="save" :disabled="!canSave"> <v-icon left x-small>mdi-check</v-icon> Erstellen </v-btn>
          </v-card-actions>
        </div>

        <!-- Right Column: Finder Dashboard (Desktop only) -->
        <work-calendar-appointment-finder
          v-if="showFinder && !$vuetify.breakpoint.smAndDown"
          class="wcd-finder-section flex-grow-1"
          :style="{
            borderLeft: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            background: isDarkMode ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }"
          :employees="employees"
          :date="selectedDate"
          @update:date="selectedDate = $event"
          :start="localStart"
          @update:start="localStart = $event"
          :col-id="localColId"
          @update:col-id="localColId = $event"
          :duration="localDuration"
          :is-dark-mode="isDarkMode"
          @select-and-close="showFinder = false" />
      </div>

      <!-- Mobile Expandable Finder Section (Tablet only, disabled on Mobile Phone) -->
      <v-expand-transition v-if="$vuetify.breakpoint.smAndDown && !$vuetify.breakpoint.xs">
        <work-calendar-appointment-finder
          v-if="showFinder"
          class="wcd-mobile-finder-section pa-3"
          style="
            border-top: 1px solid rgba(255, 255, 255, 0.08);
            background: rgba(255, 255, 255, 0.01);
            height: 450px;
            display: flex;
            flex-direction: column;
          "
          :employees="employees"
          :date="selectedDate"
          @update:date="selectedDate = $event"
          :start="localStart"
          @update:start="localStart = $event"
          :col-id="localColId"
          @update:col-id="localColId = $event"
          :duration="localDuration"
          :is-dark-mode="isDarkMode"
          @select-and-close="showFinder = false" />
      </v-expand-transition>
    </v-card>
  </v-dialog>
</template>

<script>
import UserSearch from "@/components/userSearch"
import WorkCalendarAppointmentFinder from "./WorkCalendarAppointmentFinder"
import moment from "moment"

export default {
  name: "WorkCalendarDropDialog",
  components: { UserSearch, WorkCalendarAppointmentFinder },
  props: {
    value: { type: Boolean, default: false },
    data: { type: Object, default: null },
    employees: { type: Array, default: () => [] },
    currentDate: { type: String, default: "" },
    isDarkMode: { type: Boolean, default: true },
  },
  data() {
    return {
      localColId: null,
      localStart: "",
      localDuration: 60,
      localDesc: "",
      localTitle: "",
      localIcon: "",
      selectedCustomer: null,
      guestData: {
        firstName: "",
        lastName: "",
        email: "",
      },
      noAccountMode: false,
      noAccountInfo: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      showSecondPerson: false,
      selectedCustomer2: null,
      noAccountMode2: false,
      noAccountInfo2: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      baseDuration: 60,
      contractMode: "guest",

      showFinder: false,
      selectedDate: null,
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val) {
          this.localColId = val.col?.id || null
          this.localStart = val.time
          this.baseDuration = val.template.duration || 60
          this.localDuration = this.baseDuration
          this.localDesc = val.description || ""
          this.localTitle = val.template.title || ""
          this.localIcon = val.template.icon || "mdi-calendar"
          this.selectedCustomer = null
          this.selectedCustomer2 = null
          this.noAccountMode = false
          this.noAccountMode2 = false
          this.showSecondPerson = false
          this.noAccountInfo = { firstName: "", lastName: "", phone: "", email: "" }
          this.noAccountInfo2 = { firstName: "", lastName: "", phone: "", email: "" }
          this.guestData = { firstName: "", lastName: "", email: "" }
          this.contractMode = "guest"

          this.selectedDate = val.date || this.currentDate || moment().format("YYYY-MM-DD")
          this.showFinder = false
        }
      },
    },
    value(val) {
      if (val) {
        this.selectedDate = this.data?.date || this.currentDate || moment().format("YYYY-MM-DD")
        this.showFinder = false
      }
    },
  },
  computed: {
    calculatedEnd() {
      if (!this.localStart) return "-"
      return moment(this.localStart, "HH:mm").add(this.localDuration, "minutes").format("HH:mm")
    },
    isTouchDevice() {
      if (typeof window === "undefined") return false
      return "ontouchstart" in window || navigator.maxTouchPoints > 0
    },
    selectedEmployeeName() {
      if (!this.employees || !this.localColId) return this.data?.col?.firstName || "Mitarbeiter"
      const emp = this.employees.find((e) => e.id === this.localColId)
      return emp ? emp.name : this.data?.col?.firstName || "Mitarbeiter"
    },
    canSave() {
      if (!this.localColId) {
        return false
      }
      if (this.data?.template.requiresCustomer) {
        const hasCustomer1 = this.noAccountMode
          ? this.noAccountInfo.firstName && this.noAccountInfo.lastName && this.noAccountInfo.phone
          : !!this.selectedCustomer
        if (!hasCustomer1) return false

        if (this.showSecondPerson) {
          const hasCustomer2 = this.noAccountMode2
            ? this.noAccountInfo2.firstName && this.noAccountInfo2.lastName && this.noAccountInfo2.phone
            : !!this.selectedCustomer2
          if (!hasCustomer2) return false
        }
      }

      if (this.data?.template.specialType === "new_contract") {
        if (this.contractMode === "guest" && (!this.guestData.firstName || !this.guestData.lastName)) {
          return false
        }
        if (this.contractMode === "existing" && !this.selectedCustomer) {
          return false
        }
      }

      if (this.data?.template?.id === "ap1" && !this.localTitle.trim()) {
        return false
      }

      if (this.isNoteRequired && !this.localDesc.trim()) {
        return false
      }

      return true
    },
    isNoteRequired() {
      return !!this.data?.template?.requiresNote
    },
    noteLabel() {
      return this.isNoteRequired ? "Notiz (Pflichtfeld) *" : "Notiz (Optional)"
    },
  },
  methods: {
    updateCustomer(value) {
      this.selectedCustomer = value
    },
    updateCustomer2(value) {
      this.selectedCustomer2 = value
    },
    addSecondPerson() {
      this.showSecondPerson = true
      this.localDuration = this.baseDuration + 30
    },
    removeSecondPerson() {
      this.showSecondPerson = false
      this.selectedCustomer2 = null
      this.noAccountMode2 = false
      this.noAccountInfo2 = { firstName: "", lastName: "", phone: "", email: "" }
      this.localDuration = this.baseDuration
    },
    toggleFinder() {
      this.showFinder = !this.showFinder
    },
    save() {
      const payload = {
        ...this.data,
        colId: this.localColId,
        time: this.localStart,
        duration: this.localDuration,
        description: this.localDesc,
        date: this.selectedDate || this.data?.date || this.currentDate,
        template: {
          ...this.data.template,
          title: this.localTitle,
          icon: this.localIcon,
        },
        customer:
          this.data?.template.specialType === "new_contract" && this.contractMode === "existing"
            ? this.selectedCustomer
            : this.noAccountMode
            ? null
            : this.selectedCustomer,
        noAccountData: this.noAccountMode ? { ...this.noAccountInfo } : null,
        guestData: this.data?.template.specialType === "new_contract" && this.contractMode === "guest" ? { ...this.guestData } : null,
        customer2: this.showSecondPerson && !this.noAccountMode2 ? this.selectedCustomer2 : null,
        noAccountData2: this.showSecondPerson && this.noAccountMode2 ? { ...this.noAccountInfo2 } : null,
        hasSecondPerson: this.showSecondPerson,
      }
      this.$emit("save", payload)
    },
  },
}
</script>
\n\n
<style scoped>
.wcd-card {
  background: rgba(22, 22, 22, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.wcd-card.light-theme {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.wcd-card.light-theme .wcd-title {
  color: #1e1e1e !important;
}
.wcd-card.light-theme .wcd-subtitle {
  color: rgba(0, 0, 0, 0.5) !important;
}
.wcd-card.light-theme .wcd-time-label {
  color: rgba(0, 0, 0, 0.5) !important;
}
.wcd-card.light-theme .wcd-toggle-btn {
  color: rgba(0, 0, 0, 0.6) !important;
  background: rgba(0, 0, 0, 0.03) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.wcd-card.light-theme .wcd-toggle-btn.active {
  color: white !important;
  background: var(--v-primary-base) !important;
  border-color: var(--v-primary-base) !important;
}
.wcd-card.light-theme .wcd-toggle-btn:hover {
  border-color: rgba(0, 0, 0, 0.15) !important;
}
.wcd-card.light-theme .wcd-finder-toggle-bar {
  background: rgba(0, 0, 0, 0.04) !important;
  color: rgba(0, 0, 0, 0.7) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.wcd-card.light-theme .wcd-finder-toggle-bar.active {
  background: var(--v-primary-base) !important;
  color: white !important;
  border-color: var(--v-primary-base) !important;
}
.wcd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
}
.wcd-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wcd-title {
  font-size: 1rem;
  font-weight: 700;
  color: white;
}
.wcd-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
}

/* Time Row */
.wcd-time-row {
  display: flex;
  gap: 10px;
}
.wcd-time-item {
  flex: 1;
}
.wcd-time-label {
  font-size: 0.6rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.wcd-time-input >>> input {
  text-align: center;
  font-weight: 700;
  font-size: 0.85rem;
}
.wcd-time-input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 36px !important;
}
.wcd-time-result {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  color: #66bb6a;
  background: rgba(76, 175, 80, 0.08);
  border-radius: 8px;
}

/* Toggle Buttons */
.wcd-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all 0.2s;
  text-transform: none;
}
.wcd-toggle-btn.active {
  color: white !important;
  background: color-mix(in srgb, var(--v-primary-base) 25%, transparent) !important;
  border-color: color-mix(in srgb, var(--v-primary-base) 50%, transparent) !important;
}
.wcd-toggle-btn:hover {
  border-color: rgba(255, 255, 255, 0.15);
}
.wcd-toggle-btn i {
  color: inherit !important;
}

/* Dual Person Badge */
.wcd-dual-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--v-primary-base) !important;
  background: color-mix(in srgb, var(--v-primary-base) 10%, transparent) !important;
  border: 1px solid color-mix(in srgb, var(--v-primary-base) 20%, transparent) !important;
}

/* Save Button */
.wcd-save-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.8rem !important;
}
.overflow-visible {
  overflow: visible !important;
}
.gap-2 {
  gap: 8px;
}
</style>
